// ============================================================
// WHAT A PERSON'S BODY PLATE SAYS about each hit location (plan body-map, P1):
// a callout in the margin per location - its name, armour, the cover's AP where
// the cover protects it, its hit-roll range - and the marks it carries.
//
// The names are DISPLAY only. A figure may call a location its own name (a
// beast's Right Fore, a tail's Upper Tail) and move its callout to the other
// side or row, but the id stays the engine's, and `Cr:` / `De:` conditions and
// the critical tables keep keying on bodyConfig's names.
//
// Pure. Zero React dependency.
// ============================================================

import { isLocCoveredForBody, type BodyConfig } from '../bodyConfig';
import { condType, COND_BODY_MAP } from '../conditions';
import type { Cover } from '../../types';

type Point = [number, number];
type Side = 'L' | 'R';

/** What marks a location: 'Cr' crippled, 'De' destroyed, 'implied' by a condition elsewhere. */
export type LocationMark = 'Cr' | 'De' | 'implied';

/** The parts of a figure's art the callouts read (engine/plate/figureArt's PlateArt has them all). */
export interface CalloutArt {
  anchors: Record<string, Point>;
  names: Partial<Record<string, string>>;
  sides: Partial<Record<string, Side>>;
  rows: Partial<Record<string, number>>;
}

/** What the callouts read off the projected Target. */
export interface CalloutTarget {
  armour: Record<string, number>;
  conditions: string[];
  cover?: Pick<Cover, 'level' | 'ap'>;
}

export interface PlateCallout {
  /** The engine's location id. */
  id: string;
  /** What the plate calls it: the figure's own name, else the body's. */
  name: string;
  side: Side;
  /** The callout's row, in the figure's frame (y down). */
  row: number;
  /** Where its leader line meets the figure. */
  anchor: Point;
  armour: number;
  /** The cover's AP when the cover protects this location, else null. */
  cover: number | null;
  /** The hit-roll range that lands here, inclusive. */
  range: [number, number];
  marks: LocationMark[];
}

/**
 * Where a humanoid's callouts sit when the figure says nothing (the canvas's layout). Facing the viewer, the figure's
 * right arm and leg are on the viewer's LEFT.
 */
const DEFAULT_PLACE: Record<string, { side: Side; row: number }> = {
  head: { side: 'R', row: 280 },
  body: { side: 'R', row: 900 },
  rightArm: { side: 'L', row: 1450 },
  leftArm: { side: 'R', row: 1450 },
  rightLeg: { side: 'L', row: 2350 },
  leftLeg: { side: 'R', row: 2350 },
};

/**
 * The marks on each location id. `Cr:Head` / `De:Right Arm` name a
 * location by its NAME in the body config; a condition with a body-map
 * entry (Blinded on the head, Snared on the legs) implies its parts.
 */
export function locationMarks(conditions: string[], bodyConfig: BodyConfig): Record<string, LocationMark[]> {
  const marks: Record<string, LocationMark[]> = {};
  const mark = (id: string, m: LocationMark) => (marks[id] ??= []).push(m);
  for (const cond of conditions) {
    const named = namedLocationMark(cond, bodyConfig);
    if (named) mark(named.id, named.mark);
    for (const partId of impliedParts(cond)) mark(partId, 'implied');
  }
  return marks;
}

/** `Cr:Head` -> the head is crippled, when the body has a location of that name. */
function namedLocationMark(cond: string, bodyConfig: BodyConfig): { id: string; mark: LocationMark } | null {
  const locMatch = cond.match(/^(Cr|De):(.+)$/);
  if (!locMatch) return null;
  const locEntry = bodyConfig.locations.find((l) => l.name === locMatch[2]);
  return locEntry ? { id: locEntry.id, mark: locMatch[1] as LocationMark } : null;
}

/** The body parts a condition marks by its nature (Blinded: the head). */
function impliedParts(cond: string): string[] {
  return COND_BODY_MAP[condType(cond)] || COND_BODY_MAP[cond] || [];
}

/** One callout per location of a person's body, in the body's order. */
export function plateCallouts(art: CalloutArt, target: CalloutTarget, config: BodyConfig): PlateCallout[] {
  const marks = locationMarks(target.conditions, config);
  const level = target.cover?.level ?? 'none';
  const coverAP = target.cover?.ap ?? 0;
  return config.locations.map(({ id, name, min, max }) => ({
    id,
    name: art.names[id] ?? name,
    side: art.sides[id] ?? DEFAULT_PLACE[id]?.side ?? 'R',
    row: art.rows[id] ?? DEFAULT_PLACE[id]?.row ?? 0,
    anchor: art.anchors[id] ?? [0, 0],
    armour: target.armour[id] ?? 0,
    cover: coverAP > 0 && isLocCoveredForBody(level, id, config) ? coverAP : null,
    range: [min, max],
    marks: marks[id] ?? [],
  }));
}

/**
 * The short name a callout carries (plan design-gaps, P1): a side becomes its letter - R ARM, L LEG, L FORE - so both
 * callout columns stand close to the art and the frame narrows round a bigger figure. Upper case, as drawn; the
 * readout keeps the whole name.
 */
export const calloutName = (name: string) =>
  name.replace(/^(Right|Left) /, (_, side: string) => `${side[0]} `).toUpperCase();

/** The places crippled or destroyed (plan design-gaps, P8): a thumbnail's red dots, round 3's ruling. */
export function hurtPlaces(conditions: string[], config: BodyConfig): string[] {
  const marks = locationMarks(conditions, config);
  return Object.keys(marks).filter((id) => damageOf(marks[id]) !== null);
}

/** What has happened to a location, worst first - its callout's tag - or null for nothing. */
export function damageOf(marks: readonly LocationMark[]): 'destroyed' | 'crippled' | null {
  if (marks.includes('De')) return 'destroyed';
  return marks.includes('Cr') ? 'crippled' : null;
}
